import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import React, { ChangeEventHandler, useState } from 'react';
import {
  CaptionText,
  DownArrowIcon,
  SelectWrapperDiv,
  StyledFloatingLabel,
  StyledNativeLabel,
  StyledNativeSelect
} from './StyledSelect';

export const UNSELECTED_OPTION_VALUE = 'unselected';

export interface ISelectOption {
  /**
   * Value representing the option
   */
  value?: string | number | boolean;
  /**
   * Displayed text
   */
  label: string;
  options?: ISelectOption[];
}

export type SelectOptionType = ISelectOption | string;

const createOptions = (label: string, value: string = label) =>
  (
    <option key={value} value={value}>
      {label}
    </option>
  ) as unknown as HTMLOptionElement;

const getOptions = (
  data: SelectOptionType[]
): (HTMLOptGroupElement | HTMLOptionElement)[] =>
  data.map(item => {
    if (typeof item === 'string') {
      return createOptions(item);
    }

    const { options, label } = item;

    if (options) {
      return (
        <optgroup key={label} label={label}>
          {getOptions(options)}
        </optgroup>
      ) as unknown as HTMLOptGroupElement;
    }

    const value = `${item.value ?? label}`;

    return createOptions(label, value);
  });

export interface ISelectProps {
  name: string;
  disabled?: boolean;
  value: string | number;
  options: SelectOptionType[];
  label?: string;
  caption?: string;
  marginTop?: string;
  isPlaceholderVisible?: boolean;
  onChange: ChangeEventHandler<HTMLSelectElement>;
}

const Select: React.FC<ISelectProps> = ({
  label,
  options,
  caption,
  value,
  onChange,
  marginTop,
  disabled,
  isPlaceholderVisible = false,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const isPlaceholder = value === UNSELECTED_OPTION_VALUE;
  const isTall = !!label;

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  return (
    <div>
      <SelectWrapperDiv $marginTop={marginTop}>
        <StyledNativeLabel
          $isTall={isTall}
          $isFocused={isFocused}
          $isDisabled={disabled}
        >
          {label && (
            <StyledFloatingLabel
              $isPlaceholder={isPlaceholder}
              $isDisabled={disabled}
            >
              {label}
            </StyledFloatingLabel>
          )}
          <DownArrowIcon
            $isTall={isTall}
            icon={faChevronUp}
            $isFocused={isFocused}
            $isDisabled={disabled}
            rotation={180}
          />
          <StyledNativeSelect
            {...rest}
            $isTall={!!label}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={onChange}
            disabled={disabled}
            value={value}
            $isPlaceholder={isPlaceholder}
            $isPlaceholderVisible={!!isPlaceholderVisible}
          >
            {getOptions(options)}
          </StyledNativeSelect>
        </StyledNativeLabel>
      </SelectWrapperDiv>
      {caption && <CaptionText>{caption}</CaptionText>}
    </div>
  );
};

export default Select;
